import {
  Chart,
  ChartData,
  ChartDataset,
  ChartEvent,
  DefaultDataPoint,
  InteractionItem,
  LayoutItem,
  Plugin,
  Scale,
} from 'chart.js';
import { ScenarioDataSetLabelV2 } from '@customer/store/apis/scenariosApi';

interface AfterEventArgs {
  event: ChartEvent;
  replay: boolean;
  changed?: boolean;
  cancelable: false;
  inChartArea: boolean;
}

interface CategoryScaleBox extends LayoutItem {
  type: 'category';
}

function isCategoryScaleBox(box: LayoutItem): box is CategoryScaleBox {
  return 'type' in box && box.type === 'category';
}

interface ValidChartEventClick extends ChartEvent {
  type: 'click';
  native: Event;
  x: number;
  y: number;
}

function isValidChartEventClick(event: ChartEvent): event is ValidChartEventClick {
  return (
    event.type === 'click' &&
    event.native instanceof Event &&
    typeof event.x === 'number' &&
    typeof event.y === 'number'
  );
}

export interface CustomBarChartDataset extends ChartDataset<'bar', DefaultDataPoint<'bar'>> {
  labelSource: string;
}

function isCustomBarChartDataset(
  dataset: ChartDataset<'bar', DefaultDataPoint<'bar'>>,
): dataset is CustomBarChartDataset {
  return 'labelSource' in dataset;
}

export interface CustomBarChartLabel extends ScenarioDataSetLabelV2 {
  label: string;
}

export type CustomBarChartData = ChartData<'bar', DefaultDataPoint<'bar'>, CustomBarChartLabel>;

type CustomBarChart = Chart<'bar', DefaultDataPoint<'bar'>, CustomBarChartLabel>;

export type OnCategorySelect = (category: CustomBarChartLabel) => void;

export type OnBarSelect = (category: CustomBarChartLabel, datasetId: string) => void;

export type OnGetValue = (value: number | string) => string;

interface BarSelectPluginOptions {
  onCategorySelect?: OnCategorySelect;
  onBarSelect?: OnBarSelect;
}

interface BarSelectHandlerProps {
  chart: CustomBarChart;
  event: ValidChartEventClick;
  inChartArea: boolean;
  options: BarSelectPluginOptions;
}

class BarSelectHandler {
  private readonly _chart: CustomBarChart;
  private readonly _event: ValidChartEventClick;
  private readonly _indexAxis: 'x' | 'y';
  private readonly _options: BarSelectPluginOptions;

  constructor({ chart, event, inChartArea, options }: BarSelectHandlerProps) {
    this._chart = chart;
    this._event = event;
    this._options = options;

    // Get the orientation of the bar chart - default to 'x'.
    this._indexAxis = chart.config.options?.indexAxis ?? 'x';

    this._init(inChartArea);
  }

  private _init(inChartArea: boolean): void {
    if (inChartArea && this._options.onBarSelect) {
      this._handleClickInsideChart();
    } else if (!inChartArea && this._options.onCategorySelect) {
      this._handleClickOutsideChart();
    }
  }

  private _handleClickInsideChart(): void {
    // Get the bars that match the click coordinates.
    const bars: InteractionItem[] = this._getIntersectionElements();

    if (bars.length) {
      const firstPoint = bars[0];
      const category: CustomBarChartLabel | undefined = this._chart.data.labels?.[firstPoint.index];
      const dataset = this._chart.data.datasets[firstPoint.datasetIndex];

      // Exit if the dataset does not matche the expected custom structure with a source label.
      if (!isCustomBarChartDataset(dataset)) return;

      const datasetId = dataset.labelSource;

      if (typeof category !== 'undefined' && typeof datasetId !== 'undefined') {
        this._selectBar(category, datasetId);
      }
    }
  }

  private _getIntersectionElements(): InteractionItem[] {
    // Get all elements that intersect with the click event.
    return this._chart.getElementsAtEventForMode(
      this._event.native, // The real event.
      'nearest', // The mode by which to find the elements.
      { intersect: true, includeInvisible: false }, // InteractionOptions: The elements must match the event coordinates.
      true, // Use the final position of the event.
    );
  }

  private _selectBar(category: CustomBarChartLabel, datasetId: string): void {
    this._options.onBarSelect?.(category, datasetId);
  }

  private _handleClickOutsideChart(): void {
    // Exit if the click is not on the category labels.
    if (!this._isClickOnCategoryLabels()) return;

    // Get the bars that are inline with the click on the specified axis.
    const inlineBars: InteractionItem[] = this._getInlineElements();

    if (inlineBars.length) {
      const firstBar: InteractionItem = inlineBars[0];
      const indexScale: Scale = this._chart.scales[this._indexAxis];

      // @ts-expect-error: The custom label object is not being recognised as the return value by the typescript compiler.
      const category: CustomBarChartLabel = indexScale.getLabelForValue(firstBar.index);

      this._selectCategoryLabel(category);
    }
  }

  private _isClickOnCategoryLabels(): boolean {
    const axis = this._indexAxis;

    // Get the category scale box from the chart and convert the LayoutItem type to a CategoryScaleBox.
    const categoryScaleBox: CategoryScaleBox | undefined = this._chart.boxes.find(
      (box: LayoutItem) => isCategoryScaleBox(box),
    );

    // Exit if there is no category scale box because that means there are no categories to select.
    if (!categoryScaleBox) return false;

    // Get the bounding box of the category scale section (the area with the category labels).
    const { left, right, top, bottom } = categoryScaleBox;

    // Get the padding around the category labels from the chart options - default to 0.
    const tickPadding = this._chart.config.options?.scales?.[axis]?.ticks?.padding ?? 0;

    // Determine the relative bounds for category label selction based on the index axis (chart orientation).
    const xMin = axis === 'y' ? left + tickPadding : left;
    const xMax = axis === 'y' ? right - tickPadding : right;
    const yMin = axis === 'y' ? top : top + tickPadding;
    const yMax = axis === 'y' ? bottom : bottom - tickPadding;

    const { x, y } = this._event;

    // Determine if the click is within the bounds of the category labels.
    return !(x < xMin || x > xMax || y < yMin || y > yMax);
  }

  private _getInlineElements(): InteractionItem[] {
    // Get all elements that are inline with the click event on the index axis.
    return this._chart.getElementsAtEventForMode(
      this._event.native, // The real event.
      'nearest', // The mode by which to find the elements.
      { intersect: false, axis: this._indexAxis, includeInvisible: true }, // InteractionOptions: Find all inline elements on the correct axis.
      true, // Use the final position of the event.
    );
  }

  private _selectCategoryLabel(category: CustomBarChartLabel): void {
    this._options.onCategorySelect?.(category);
  }
}

export const BarSelectPluginId = 'barSelectPlugin';

export const barSelectPlugin: Plugin = {
  id: BarSelectPluginId,

  afterEvent(
    chart: CustomBarChart,
    { event, inChartArea }: AfterEventArgs,
    options: BarSelectPluginOptions,
  ): void {
    if (isValidChartEventClick(event)) {
      // The handler class automatically initialises and processes the click event.
      new BarSelectHandler({ chart, event, inChartArea, options });
    }
  },
};
